import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Badge, Icon, type IconName } from '../index';
import { formatE1rm } from '@jackedlogs/shared';

import type { useTheme } from './theme';
import type { TimelineRow } from './timelineRows';
import type { FeedItem } from './timelineFeed';
import { TimelineNoteCard } from './TimelineNoteCard';
import { TimelineSetRow } from './TimelineSetRow';
import { flashKeyForRow, type FlashRow } from './timelineFlash';
import { HeaderTooltip } from './HeaderTooltip';
import { DropsetSummary } from './DropsetSummary';
import { makeTimelineRowText } from './timelineRowText';

// TimelineList (Phase 2, slice 2) — the chronological feed of real logged
// events (next/timeline-logger.md §4B), oldest at the top and newest at the
// bottom (Antenore 2026-06-01, inbox 11f01866). Presentational: it renders the
// pre-built rows (buildTimelineRows) led by a "workout started" marker derived
// from startedAt (not a stored event) — it sits ABOVE the first row because the
// session start is the earliest moment. Rows are mapped Views, not a
// nested vertical FlatList — the timeline body is already a ScrollView and
// nesting a same-orientation VirtualizedList breaks virtualization; one
// workout's set count is small, so plain rows are correct here.
//
// Tapping a row focuses that exercise in the FocusPanel (a safe edit
// affordance for now); inline per-row value editing — with the id +
// performedAt immutability rule (§7.5) — is a later slice (design §9 open Q).
export function TimelineList({
  feed,
  startedAt,
  resolveName,
  groupLabel,
  onFocusRow,
  onDeleteNote,
  bestE1rmForSlug,
  flashRow,
  locale,
  theme,
}: {
  /** Chronological feed: set rows + note mini-cards, oldest first
   *  (buildTimelineFeed). */
  feed: FeedItem[];
  startedAt: string;
  resolveName: (slug: string, fallback: string) => string;
  /** Localized "Superset N" / "Circuit N" label for a group id, or null
   *  when the row is standalone. */
  groupLabel: (
    groupId: string | null,
    kind: 'superset' | 'circuit' | null,
  ) => string | null;
  /** Refocus the row a tapped set belongs to. Takes an entryId, not a slug
   *  (LG-39): the same exercise can own several rows in one workout. */
  onFocusRow: (entryId: string) => void;
  /** Remove a draft note from the timeline (note mini-card ✕). */
  onDeleteNote: (id: string) => void;
  /** Best e1RM (kg) for a slug across recent history, or null when unknown
   *  (cold cache / no history). Drives the per-row "% of best" comparison
   *  (slice 5c). */
  bestE1rmForSlug: (exerciseId: string) => number | null;
  /** The just-failed set to flash: its row `${entryIdx}-${setIdx}-` key prefix
   *  + a bumping key. Null when nothing is flashing. */
  flashRow: FlashRow;
  locale: string;
  theme: ReturnType<typeof useTheme>;
}) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const { timeFmt, volFmt, summaryFor, summaryA11yFor } = makeTimelineRowText(
    t,
    locale,
  );

  const iconFor = (row: TimelineRow): IconName => {
    if (row.groupId) return 'repeat';
    if (row.kind === 'cardio') return 'run';
    if (row.kind === 'timed') return 'timer';
    return 'dumbbell';
  };

  // e1RM stat line (slice 5c): the set's estimated 1RM + how it compares to the
  // exercise's best recent e1RM. We compare to BEST (not last) and frame it as
  // "% of best" so an ordinary sub-max set reads as relative intensity ("room
  // to push") rather than a regression; meeting or beating best is highlighted.
  const renderE1rm = (row: TimelineRow) => {
    if (row.e1rmKg == null) return null;
    const value = formatE1rm(row.e1rmKg, row.unit, locale);
    const best = bestE1rmForSlug(row.exerciseId);
    let comparison = null;
    if (best != null && best > 0) {
      if (row.e1rmKg >= best) {
        comparison = (
          <View style={styles.e1rmBestRow}>
            <Icon
              name="trend-up"
              size={theme.typography.sizes.xs}
              color={theme.palette.pr}
            />
            <Text style={styles.e1rmBest}>
              {t('mobile.timeline.e1rmNewBest', { defaultValue: 'best' })}
            </Text>
          </View>
        );
      } else {
        const pct = Math.round((row.e1rmKg / best) * 100);
        comparison = (
          <Text style={styles.e1rmPct}>
            {t('mobile.timeline.e1rmPctOfBest', {
              pct,
              defaultValue: `${pct}% of best`,
            })}
          </Text>
        );
      }
    }
    return (
      <View style={styles.statsRow}>
        <Text style={styles.e1rmValue}>
          {t('mobile.timeline.e1rmValue', {
            value,
            defaultValue: `e1RM ${value}`,
          })}
        </Text>
        {comparison}
      </View>
    );
  };

  const hasE1rm = feed.some(
    (it) => it.type === 'set' && it.set.e1rmKg != null,
  );

  const renderSetRow = (row: TimelineRow, key: string) => {
    const label = groupLabel(row.groupId, row.groupKind);
    // The just-failed row flashes (2026-07-20). flashKeyForRow matches the
    // failed set's prefix against the bare ROW key (`row.key`), never the
    // `set-`-wrapped FeedItem `key` param — see timelineFlash.ts.
    const flashKey = flashKeyForRow(row.key, flashRow);
    return (
      <TimelineSetRow
        key={key}
        onPress={() => onFocusRow(row.entryId)}
        grouped={row.groupId != null}
        flashKey={flashKey}
        palette={theme.palette}
        rowStyle={styles.row}
        groupedStyle={styles.rowGrouped}
        pressedStyle={styles.rowPressed}
      >
        <View style={styles.icon}>
          <Icon
            name={iconFor(row)}
            size={styles.icon.fontSize}
            color={theme.palette.ink2}
          />
        </View>
        <View style={styles.rowBody}>
          <View style={styles.rowTop}>
            <Text style={styles.time}>{timeFmt(row.completedAt)}</Text>
            <Text style={styles.name} numberOfLines={1}>
              {resolveName(row.exerciseId, row.exerciseName)}
            </Text>
            {row.pr ? (
              <Badge
                theme={theme}
                tone="pr"
                icon="trophy"
                a11yLabel={t('mobile.summary.prBadge', {
                  defaultValue: 'personal record',
                })}
              />
            ) : null}
            {row.regression ? (
              <Badge
                theme={theme}
                tone="danger"
                icon="trend-down"
                a11yLabel={t('workout.maxDownA11y', {
                  defaultValue: 'MAX may have dropped',
                })}
              />
            ) : null}
            {row.fail ? (
              <Badge
                theme={theme}
                tone="danger"
                icon="x"
                label={t('mobile.logger.failBadge', {
                  defaultValue: 'Failed',
                })}
                a11yLabel={t('mobile.logger.failBadgeA11y', {
                  defaultValue: 'failed set',
                })}
              />
            ) : null}
          </View>
          <View style={styles.rowBottom}>
            {label ? (
              // numberOfLines matches RosterStrip's identical tag: since PE-30
              // this label can be a coach-typed name (up to 60 chars), and
              // unguarded it wrapped onto extra lines HERE — repeated per set
              // of the group — inflating every row of a named superset.
              <Text style={styles.groupTag} numberOfLines={1}>
                {label}
              </Text>
            ) : null}
            <Text style={styles.summary} accessibilityLabel={summaryA11yFor(row)}>
              {summaryFor(row)}
            </Text>
            {row.volume != null ? (
              <Text
                style={styles.volume}
                accessibilityLabel={t('mobile.timeline.setVolumeA11y', {
                  value: volFmt(row.volume),
                  unit: row.unit,
                  defaultValue: `Set volume {{value}} {{unit}}`,
                })}
              >
                {t('mobile.timeline.setVolume', {
                  value: volFmt(row.volume),
                  unit: row.unit,
                  defaultValue: `Σ {{value}} {{unit}}`,
                })}
              </Text>
            ) : null}
          </View>
          {row.dropRounds && row.dropRounds.length > 0 && row.dropsetVariant ? (
            <DropsetSummary
              variant={row.dropsetVariant}
              steps={row.dropRounds}
              unit={row.unit}
              t={t}
              theme={theme}
            />
          ) : null}
          {renderE1rm(row)}
        </View>
      </TimelineSetRow>
    );
  };

  return (
    <View style={styles.list}>
      <View style={styles.startRow}>
        <View style={styles.icon}>
          <Icon
            name="circle"
            size={styles.icon.fontSize}
            color={theme.palette.complete}
            fill={theme.palette.complete}
          />
        </View>
        <Text style={styles.startText}>
          {t('mobile.timeline.started', { defaultValue: 'Workout started' })}
          {'  '}
          {timeFmt(startedAt)}
        </Text>
      </View>
      {/* One-time e1RM legend (slice 5c) — explains the per-row stat the first
          time a weighted set appears. Tap-to-explain via HeaderTooltip so it
          respects the Hints toggle and doesn't repeat on every row. */}
      {hasE1rm ? (
        <View style={styles.legendRow}>
          <HeaderTooltip
            label={t('mobile.timeline.e1rmLegend', { defaultValue: 'e1RM' })}
            a11yLabel={t('mobile.timeline.e1rmLegendA11y', {
              defaultValue: 'What is e1RM?',
            })}
            tip={t('mobile.timeline.e1rmTip', {
              defaultValue:
                'e1RM is your estimated 1-rep max for the set (Epley, reps ≤ 12). “% of best” compares it to your best e1RM in recent history — lower means room to push; ↑ best means a new estimated record.',
            })}
            leadingIcon="info"
            labelStyle={styles.legendLabel}
            cellStyle={styles.legendCell}
            theme={theme}
          />
        </View>
      ) : null}
      {feed.map((item) =>
        item.type === 'set' ? (
          renderSetRow(item.set, item.key)
        ) : (
          <TimelineNoteCard
            key={item.key}
            note={item.note}
            time={timeFmt(item.note.performedAt)}
            onDelete={onDeleteNote}
            t={t}
            theme={theme}
          />
        ),
      )}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    list: { gap: s.s2 },
    row: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: s.s3,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s4,
      paddingVertical: s.s3,
    },
    rowGrouped: {
      borderLeftColor: p.ink3,
      borderLeftWidth: 3,
    },
    rowPressed: { backgroundColor: p.surface2 },
    icon: { fontSize: ty.sizes.base, marginTop: 1 },
    rowBody: { flex: 1, gap: 2 },
    rowTop: { flexDirection: 'row', alignItems: 'center', gap: s.s2 },
    time: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontVariant: ['tabular-nums'],
    },
    name: {
      flex: 1,
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: '600',
    },
    // Wraps: a bodyweight row gained BOTH a load and a Σ chip with IB-9, so a
    // grouped one now runs groupTag + summary + Σ on one line. Unwrapped, the
    // narrow-screen overflow would silently clip the number the fix exists to
    // show ([[feedback_user_visual_accessibility]] — readability wins over the
    // one-line silhouette).
    rowBottom: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: s.s2,
    },
    groupTag: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
    },
    summary: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontVariant: ['tabular-nums'],
    },
    // Per-set tonnage chip — muted accent, distinct from the reps·weight
    // summary so the ×2 on unilateral/two-implement work reads at a glance.
    volume: {
      color: p.ink3,
      fontSize: ty.sizes.sm,
      fontWeight: '600',
      fontVariant: ['tabular-nums'],
    },
    startRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingHorizontal: s.s4,
      paddingVertical: s.s2,
    },
    startText: { color: p.ink3, fontSize: ty.sizes.sm },
    // e1RM legend (once, above the rows) + per-row stat line.
    legendRow: {
      flexDirection: 'row',
      paddingHorizontal: s.s4,
    },
    legendCell: { alignItems: 'flex-start' },
    legendLabel: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
    },
    statsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      marginTop: 2,
    },
    e1rmValue: {
      color: p.ink2,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
      fontVariant: ['tabular-nums'],
    },
    // New estimated best — the word plus a trend-up mark in the PR gold, the
    // same status colour the trophy uses. Was the literal '↑' baked into all 5
    // locale strings: a font-drawn glyph, the same defect as the «⬇️» IB-10
    // reported, so it went the same way.
    e1rmBestRow: { flexDirection: 'row', alignItems: 'center', gap: 2 },
    e1rmBest: {
      color: p.ink,
      fontSize: ty.sizes.xs,
      fontWeight: '700',
    },
    // Relative intensity vs best — muted, informational ("room to push").
    e1rmPct: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontVariant: ['tabular-nums'],
    },
  });
}
